import {
  isTableDensity,
  isTableFontSize,
  type TableDisplayPref
} from 'components/Table/tableDisplay'

import type { TableColumnPref } from './types'

const KEY = (merchantUserId: string, tableId: string) =>
  `cb:table-cols:${merchantUserId}:${tableId}`

export function readPref(
  merchantUserId: string,
  tableId: string
): TableColumnPref | null {
  if (!merchantUserId || typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(KEY(merchantUserId, tableId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof parsed.visibility !== 'object' ||
      !Array.isArray(parsed.order)
    ) {
      return null
    }
    // Sanitize the optional display block — drop it entirely if malformed so a
    // corrupted value can never reach the rendering layer.
    let display: TableDisplayPref | undefined
    if (
      parsed.display &&
      isTableFontSize(parsed.display.fontSize) &&
      isTableDensity(parsed.display.density)
    ) {
      display = {
        fontSize: parsed.display.fontSize,
        density: parsed.display.density
      }
    }
    return {
      visibility: parsed.visibility,
      order: parsed.order,
      ...(display && { display })
    } as TableColumnPref
  } catch {
    // eslint-disable-next-line no-console
    console.warn(
      `[table-columns] failed to parse pref for ${merchantUserId}:${tableId}`
    )
    return null
  }
}

export function writePref(
  merchantUserId: string,
  tableId: string,
  pref: TableColumnPref
): void {
  if (!merchantUserId || typeof window === 'undefined') return
  try {
    window.localStorage.setItem(
      KEY(merchantUserId, tableId),
      JSON.stringify(pref)
    )
  } catch {
    // eslint-disable-next-line no-console
    console.warn(
      `[table-columns] failed to write pref for ${merchantUserId}:${tableId}`
    )
  }
}

export function clearPref(merchantUserId: string, tableId: string): void {
  if (!merchantUserId || typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(KEY(merchantUserId, tableId))
  } catch {
    /* noop */
  }
}

export function clearAllPrefs(merchantUserId: string): void {
  if (!merchantUserId || typeof window === 'undefined') return
  try {
    const prefix = `cb:table-cols:${merchantUserId}:`
    const keys: string[] = []
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i)
      if (k && k.startsWith(prefix)) keys.push(k)
    }
    keys.forEach((k) => window.localStorage.removeItem(k))
  } catch {
    /* noop */
  }
}

// Backend e a fonte da verdade: limpa o cache local do usuario e regrava
// apenas o que veio do payload de login. Entradas malformadas sao ignoradas.
export function hydratePrefs(
  merchantUserId: string,
  prefsByTable: Record<string, unknown> | null | undefined
): void {
  if (!merchantUserId || typeof window === 'undefined') return
  clearAllPrefs(merchantUserId)
  if (!prefsByTable || typeof prefsByTable !== 'object') return
  Object.entries(prefsByTable).forEach(([tableId, value]) => {
    if (
      value &&
      typeof value === 'object' &&
      typeof (value as { visibility?: unknown }).visibility === 'object' &&
      Array.isArray((value as { order?: unknown }).order)
    ) {
      writePref(merchantUserId, tableId, value as TableColumnPref)
    }
  })
}
